import { blueDarkest, graphOldRose, primaryDarkest, purpleDarkest, redDarkest } from '../../../../styles/design-tokens';
import type { Document } from '../../flora.type';

const DOC_TYPE_CONFIG: Record<Document['doc_type'], { icon: string; color: string; label: string }> = {
  pptx: { icon: 'vscode-icons:file-type-powerpoint', color: graphOldRose, label: 'PowerPoint' },
  xlsx: { icon: 'vscode-icons:file-type-excel', color: primaryDarkest, label: 'Excel' },
  docx: { icon: 'vscode-icons:file-type-word', color: blueDarkest, label: 'Word' },
  pdf: { icon: 'vscode-icons:file-type-pdf2', color: redDarkest, label: 'PDF' },
  png: { icon: 'vscode-icons:file-type-image', color: purpleDarkest, label: 'PNG' },
  jpg: { icon: 'vscode-icons:file-type-image', color: purpleDarkest, label: 'JPG' },
  csv: { icon: 'vscode-icons:file-type-csv', color: primaryDarkest, label: 'CSV' },
  tsv: { icon: 'vscode-icons:file-type-csv', color: primaryDarkest, label: 'TSV' },
  txt: { icon: 'vscode-icons:file-type-text', color: blueDarkest, label: 'Text' },
  md: { icon: 'vscode-icons:file-type-markdown', color: purpleDarkest, label: 'Markdown' },
  json: { icon: 'vscode-icons:file-type-json', color: graphOldRose, label: 'JSON' },
};

/**
 * Plain-text document types the app renders itself from the document's text
 * (markdown, a table, pretty-printed JSON, or plain text) rather than through
 * the server-side PDF conversion. Rendered as text, never as HTML or in a frame.
 */
const TEXT_PREVIEW_DOC_TYPES: ReadonlySet<Document['doc_type']> = new Set(['csv', 'tsv', 'md', 'json', 'txt']);

/**
 * Document types that open in the in-app preview side panel instead of
 * downloading: Word and Excel, which the backend converts to PDF server-side,
 * plus the plain-text types in {@link TEXT_PREVIEW_DOC_TYPES}. Every other type
 * has no in-app preview and only downloads.
 */
const PREVIEWABLE_DOC_TYPES: ReadonlySet<Document['doc_type']> = new Set(['docx', 'xlsx', ...TEXT_PREVIEW_DOC_TYPES]);

/**
 * Whether a document type can be shown in the in-app preview side panel.
 *
 * @param {Document['doc_type']} docType - The document's file-type key
 * @returns {boolean} True for Word, Excel, and the plain-text types; false for every other type
 */
function isPreviewableDocType(docType: Document['doc_type']): boolean {
  return PREVIEWABLE_DOC_TYPES.has(docType);
}

/**
 * Whether a document type is previewed from its text instead of as a PDF.
 *
 * @param {string | undefined} docType - The document's file-type key
 * @returns {boolean} True for CSV, TSV, Markdown, JSON, and plain text
 */
function isTextPreviewDocType(docType: string | undefined): boolean {
  return docType != null && TEXT_PREVIEW_DOC_TYPES.has(docType as Document['doc_type']);
}

/**
 * Formats a file size in bytes to a human readable string.
 * @param {number} bytes - The file size in bytes
 * @returns {string} The formatted file size
 */
function formatFileSize(bytes?: number): string {
  if (Number.isNaN(bytes) || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Collapses repeated occurrences of the same Flora-produced document within a
 * single turn down to one entry, keeping the latest revision.
 *
 * Flora appends a new `Document` to the message every time it creates, edits, or
 * re-inserts a file during a turn (see the websocket document handler in
 * `flora-hooks.ts`), so a doc created and then revised N times yields N+1 array
 * entries that all share one `document_id`. Rendering each as its own card is
 * confusing; this keeps a single card per document so iteration shows one block.
 *
 * Identity is `document_id` when present, else `filename` (a doc still being
 * created may not have an id yet). A provisional occurrence with no id is
 * reconciled with the later occurrence that carries the same filename once its
 * id arrives, so the create-then-identify sequence collapses to one card instead
 * of leaving a filename-keyed and an id-keyed entry side by side. Two occurrences
 * that both carry ids are never merged — same filename, different ids means two
 * genuinely different files.
 *
 * Among occurrences of one document, the kept entry is the one with the highest
 * `revision`; on a tie, or when `revision` is absent, the later (more recently
 * appended) occurrence wins. First-seen order is preserved so distinct documents
 * stay in the order Flora produced them and a revised doc's card does not jump
 * position mid-turn.
 *
 * Null/undefined elements are skipped rather than dereferenced: the source
 * arrays are cast (`as Document[]`) off unvalidated websocket frames and BE
 * history, so a holey element must not crash the render.
 *
 * @param {Document[]} documents - The raw per-message documents, in append order
 * @returns {Document[]} One document per unique file, latest revision, in first-seen order
 */
function dedupeDocumentsByLatestRevision(documents: Document[]): Document[] {
  const indexById = new Map<string, number>();
  // Only id-less (provisional) entries are tracked here; a slot is dropped from
  // this map once an id claims it, so a genuinely different id-less file that
  // reuses the name still gets its own card.
  const provisionalIndexByFilename = new Map<string, number>();
  const deduped: Document[] = [];

  // Keep the higher-revision occupant of a slot; on a tie the later (incoming)
  // occurrence wins, matching first-seen ordering with latest-content semantics.
  const keepLatest = (slot: number, candidate: Document) => {
    const candidateRevision = candidate.revision ?? Number.NEGATIVE_INFINITY;
    const existingRevision = deduped[slot].revision ?? Number.NEGATIVE_INFINITY;
    if (candidateRevision >= existingRevision) {
      deduped[slot] = candidate;
    }
  };

  for (const document of documents) {
    if (!document) continue;
    const id = document.document_id;
    const filename = document.filename;

    if (id) {
      const idSlot = indexById.get(id);
      if (idSlot !== undefined) {
        keepLatest(idSlot, document);
        continue;
      }
      // No id-keyed match yet: adopt a same-filename provisional entry if one is
      // still waiting for its id, so the identified doc supersedes it in place.
      const provisionalSlot = provisionalIndexByFilename.get(filename);
      if (provisionalSlot !== undefined && !deduped[provisionalSlot].document_id) {
        keepLatest(provisionalSlot, document);
        indexById.set(id, provisionalSlot);
        provisionalIndexByFilename.delete(filename);
        continue;
      }
      indexById.set(id, deduped.length);
      deduped.push(document);
      continue;
    }

    // Id-less: only dedupe against other still-provisional occurrences by name.
    const provisionalSlot = provisionalIndexByFilename.get(filename);
    if (provisionalSlot !== undefined) {
      keepLatest(provisionalSlot, document);
      continue;
    }
    provisionalIndexByFilename.set(filename, deduped.length);
    deduped.push(document);
  }

  return deduped;
}

export {
  dedupeDocumentsByLatestRevision,
  DOC_TYPE_CONFIG,
  formatFileSize,
  isPreviewableDocType,
  isTextPreviewDocType,
  PREVIEWABLE_DOC_TYPES,
  TEXT_PREVIEW_DOC_TYPES,
};
